import { makeAutoObservable } from 'mobx';
import Query from './Query';

export default class QueryResponse<T> {
  limit = 20;
  offset = 0;
  itemCount = -1;
  totalCount = -1;

  items: T[] = [];

  constructor(
    private query: Query<any>,
    dto: QueryResponseDto<T>,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });

    if (Array.isArray(dto)) {
      let items = dto;

      this.limit = query.pagination.limit;
      this.offset = query.pagination.offset;
      this.itemCount = items.length;

      if (this.itemCount > this.limit) {
        items = items.slice(0, this.limit);
      }

      this.items = items;
    } else if (dto.items) {
      this.limit = dto.limit ?? query.pagination.limit;
      this.offset = dto.offset ?? query.pagination.offset;

      this.itemCount = dto.items.length;
      this.totalCount = dto.total;

      this.items = dto.items;
    } else {
      this.limit = query.pagination.limit;
      this.offset = query.pagination.offset;
      this.itemCount = 0;
      this.items = [];
    }
  }

  get page() {
    return Math.floor(this.offset / this.limit) + 1;
  }

  get numberOfPages() {
    return Math.ceil(this.totalCount / this.limit);
  }

  get hasPrevPage() {
    return this.page > 1;
  }

  get hasNextPage() {
    if (this.totalCount === -1) {
      return this.itemCount > this.items.length;
    }

    return this.offset + this.limit < this.totalCount;
  }

  async toPreviousPage() {
    await this.loadPage(this.page - 1);
  }

  async toNextPage() {
    await this.loadPage(this.page + 1);
  }

  setPage(pageNumber: number) {
    this.query.pagination.offset = this.limit * (pageNumber - 1);
  }

  setLimit(limit: number) {
    this.query.pagination.limit = limit;
  }

  async loadPage(pageNumber: number) {
    this.setPage(pageNumber);
    return this.query.execute();
  }

  async loadWithLimit(limit: number) {
    this.setLimit(limit);
    this.setPage(1);
    return this.query.execute();
  }
}

export interface QueryResponsePaginatedDto<T = any> {
  items: T[];
  limit: number;
  offset: number;
  total: number;
}

export type QueryResponseDto<T = any> = QueryResponsePaginatedDto<T> | T[];
